import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["nonMerchandiseIdIdentifier", "fallbackSeleniumIdentifier", "queryParamTerm", "productConfigurations", "skipInterstitialPage", "redirectQueryParams"];
import FormattedMessage from 'I18n/components/FormattedMessage';
import { UPGRADE_SOURCE } from 'pricing-pages-lib/constants/QueryParams';
import { PurchaseMotionCTA } from 'purchase-motions-components/components/PurchaseMotionCTA/PurchaseMotionCTA';
import { useSelector } from 'react-redux';
import { BUY_ADDITIONAL_CONTACTS, BUY_ADDITIONAL_SEATS, BUY_TOUCHLESS, TRIAL } from 'ui-addon-upgrades/_core/purchaseMotions/PurchaseMotionTypes';
// @ts-expect-error dependency missing types
import UpgradeProductToApiNameMap from 'self-service-api/constants/UpgradeProductToApiNameMap';
import UIPurchaseMotionButton from 'ui-addon-upgrades/button/UIPurchaseMotionButton';
import { SSBU_0004_GROUP_PARAMETER_OPTIONS, SSBU_0004_TREATMENT_KEY, SSBU_0008_GROUP_PARAMETER_OPTIONS, SSBU_0008_TREATMENT_KEY } from '../../../constants/experimentKeys';
import { MERCH_CARD, PRODUCT_PAGE_FEATURE_TABLE_HEADER, SSM0010_RECOMMENDED_SALES_FOOTER, SSM0010_RECOMMENDED_SALES_HEADER_BANNER } from '../../../constants/tracking/TrackingSources';
import { useDisplayedCurrencyCode } from '../../../hooks/useCurrencies';
import useIsCheckoutUIFreeToProEnabled from '../../../hooks/useIsCheckoutUIFreeToProEnabled';
import useIsCheckoutUIFreeToStarterEnabled from '../../../hooks/useIsCheckoutUIFreeToStarterEnabled';
import { usePurchaseOrderGeneration } from '../../../hooks/usePurchaseOrderGeneration';
import { useTrackingFlow } from '../../../hooks/useTrackingFlow';
import { useAppSelector } from '../../../reducers';
import { isAppAuthSelector } from '../../../selectors/AuthSelectors';
import { isUngatedForMTVTrialPurchaseMotionSelector, isUngatedForSeatsSelector } from '../../../selectors/GatingSelectors';
import { isMarketingContactsSidegradeSelector } from '../../../selectors/MarketingContactsSelectors';
import { getFirstHubSkuIdFromProductConfigurations, nonRecurringProductConfigurationsSelector, offerProductConfigurationsSelector } from '../../../selectors/ProductConfigsSelectors';
import { productsFromProductInventorySelector } from '../../../selectors/ProductInventorySelectors';
import { crmSuiteProductLevelPreSeatsAndSeatsOnlySelector, productForSkuIdSelector, rawProductListSelector } from '../../../selectors/ProductsSelectors';
import { trackingProductNameSelector } from '../../../selectors/TrackingSelectors';
import { Metrics } from '../../../utils/metrics';
import { trackBuyButtonClick } from '../../../utils/tracking/trackingEventUtils';
import { useQueryParamFromLocation } from '../../../utils/urls/appQueryParamUtils';
import InterstitialMarketingContactsPurchaseMotionButton from '../../interstitial/InterstitialMarketingContactsPurchaseMotionButton';
import ToInterstitialPurchaseMotionButtonWrapper from './ToInterstitialPurchaseMotionButtonWrapper';
import { jsx as _jsx } from "react/jsx-runtime";
const setupMetricCounter = upgradeData => {
  Metrics.counter('buy-now-clicked', {
    source: upgradeData && upgradeData.uniqueId || 'PMBW'
  }).increment();
};
const filteredPropsForPMB = props => {
  const propsToPass = _objectWithoutPropertiesLoose(props, _excluded);
  return propsToPass;
};
const PurchaseMotionButtonWrapper = props => {
  var _props$purchaseMotion;
  const {
    productConfigurations = [],
    nonMerchandiseIdIdentifier,
    fallbackSeleniumIdentifier,
    queryParamTerm,
    onClick,
    redirectQueryParams,
    skipInterstitialPage,
    trackingSource,
    purchaseMotionLocation
  } = props;
  const displayedCurrencyCode = useDisplayedCurrencyCode();
  const purchaseMotion = (_props$purchaseMotion = props.purchaseMotion) !== null && _props$purchaseMotion !== void 0 ? _props$purchaseMotion : props.purchaseMotions[props.level - 1];
  const crmSuiteProductLevel = useSelector(state => crmSuiteProductLevelPreSeatsAndSeatsOnlySelector(state, {
    skuIds: productConfigurations.map(({
      skuId
    }) => skuId)
  }));
  const crmSuiteSeleniumId = crmSuiteProductLevel && `crm-suite-${crmSuiteProductLevel}`;
  const apiName = useSelector(state => {
    const hubSkuId = getFirstHubSkuIdFromProductConfigurations(state, {
      productConfigurations
    });
    const product = hubSkuId && productForSkuIdSelector(state, {
      skuId: hubSkuId
    });
    return product && product.apiName;
  });
  const isMarketingContactsSidegrade = useAppSelector(state => isMarketingContactsSidegradeSelector(state, productConfigurations));
  const isAppAuth = useAppSelector(state => isAppAuthSelector(state));
  const isSeatsUngated = useAppSelector(state => isUngatedForSeatsSelector(state));
  const isMTVTrialButtonUngated = useAppSelector(state => isUngatedForMTVTrialPurchaseMotionSelector(state));
  const isPreSeats = !isSeatsUngated;
  const productIdentifier = crmSuiteSeleniumId || nonMerchandiseIdIdentifier || apiName || props.upgradeData && props.upgradeData.apiName || fallbackSeleniumIdentifier;
  const offerProductConfigurations = useSelector(state => offerProductConfigurationsSelector(state, {
    productConfigurations,
    queryParamTerm
  }));
  const nonRecurringProductConfigurations = useSelector(state => nonRecurringProductConfigurationsSelector(state, {
    productConfigurations
  }));
  const rawProductList = useSelector(rawProductListSelector);
  const isCheckoutUIFreeToStarterEnabled = useIsCheckoutUIFreeToStarterEnabled(productConfigurations);
  const isCheckoutUIFreeToProEnabled = useIsCheckoutUIFreeToProEnabled(productConfigurations);
  const isCheckoutUIFreeToStarterOrProEnabled = purchaseMotion === BUY_TOUCHLESS && (isCheckoutUIFreeToStarterEnabled || isCheckoutUIFreeToProEnabled);
  const {
    requestedProducts,
    terms
  } = usePurchaseOrderGeneration({
    productConfigurations,
    queryParamTerm
  });
  const trackingProductNames = useSelector(state => productConfigurations.map(({
    skuId
  }) => trackingProductNameSelector(state, {
    skuId
  })));
  const ownedProducts = useSelector(state => productsFromProductInventorySelector(state)).map(p => p.apiName);
  const flow = useTrackingFlow({
    productConfigurations,
    purchaseMotion
  });
  const propsToPass = Object.assign({}, props, {
    currencyCode: displayedCurrencyCode,
    requestedProducts,
    terms,
    nonRecurringProductConfigurations,
    offerProductConfigurations,
    'data-selenium-id': `${trackingSource}-${productIdentifier}-${purchaseMotion}-button`,
    nonMerchandiseIdIdentifier: crmSuiteSeleniumId || nonMerchandiseIdIdentifier,
    truncate: true,
    shouldShowErrorAlert: true,
    products: rawProductList,
    location: purchaseMotionLocation
  }, purchaseMotion === BUY_TOUCHLESS && {
    onClick: e => {
      if (onClick) {
        onClick(e);
      }
      setupMetricCounter(propsToPass.upgradeData);

      // Allows for proper tracking of SSBU-0004 and SSBU-0008 experiments in variation group.
      if (isCheckoutUIFreeToStarterOrProEnabled) {
        trackBuyButtonClick({
          action: `${isCheckoutUIFreeToStarterEnabled ? SSBU_0004_TREATMENT_KEY : SSBU_0008_TREATMENT_KEY} ${isCheckoutUIFreeToStarterEnabled ? SSBU_0004_GROUP_PARAMETER_OPTIONS.variation : SSBU_0008_GROUP_PARAMETER_OPTIONS.variation} buy button clicked`,
          purchaseMotion,
          productNames: trackingProductNames,
          ownedProducts,
          trackingSource: props.trackingSource,
          screen: props.screen,
          flow
        });
      }
    }
  }, {
    passthroughProps: {
      [BUY_TOUCHLESS]: {
        redirectQueryParams: isCheckoutUIFreeToStarterOrProEnabled ? Object.assign({}, redirectQueryParams, {
          isAccordionFreeToStarterOrProEnabled: true
        }) : redirectQueryParams
      },
      [TRIAL]: {
        onClick: e => {
          if (onClick) {
            onClick(e);
          }
        }
      }
    }
  });
  const upgradeSource = useQueryParamFromLocation(UPGRADE_SOURCE);
  if (upgradeSource) {
    propsToPass.upgradeData = Object.assign({}, propsToPass.upgradeData, {
      upgradeSource
    });
  }
  if (isCheckoutUIFreeToStarterOrProEnabled) {
    return /*#__PURE__*/_jsx(UIPurchaseMotionButton, Object.assign({}, filteredPropsForPMB(propsToPass)));
  }

  // Pre-seats and Seats users should go to a common interstitial page to buy additional contacts
  if (isAppAuth && purchaseMotionLocation !== 'PRICING_PAGE_INTERSTITIAL' && purchaseMotion === BUY_ADDITIONAL_CONTACTS && !skipInterstitialPage) {
    return /*#__PURE__*/_jsx(ToInterstitialPurchaseMotionButtonWrapper, Object.assign({}, propsToPass));
  }

  // Seats users should see a button that takes them to the interstitial page if not already on the interstitial page
  if (isSeatsUngated && isAppAuth && purchaseMotionLocation !== 'PRICING_PAGE_INTERSTITIAL' && (purchaseMotion === BUY_TOUCHLESS || purchaseMotion === BUY_ADDITIONAL_SEATS) && !skipInterstitialPage) {
    return /*#__PURE__*/_jsx(ToInterstitialPurchaseMotionButtonWrapper, Object.assign({}, propsToPass));
  }

  // Pre-seats and Seats users on the interstitial page should see a button that take them to checkout for marketing contacts
  if (purchaseMotionLocation === 'PRICING_PAGE_INTERSTITIAL' && purchaseMotion === BUY_ADDITIONAL_CONTACTS && productConfigurations.length > 0) {
    return /*#__PURE__*/_jsx(InterstitialMarketingContactsPurchaseMotionButton, {
      offerProductConfigurations: offerProductConfigurations,
      nonRecurringProductConfigurations: nonRecurringProductConfigurations,
      upgradeData: props.upgradeData,
      productConfigurations: productConfigurations,
      onClick: propsToPass.onClick,
      trackingSource: props.trackingSource,
      requestedProducts: propsToPass.requestedProducts,
      terms: propsToPass.terms
    });
  }
  if (isPreSeats && isAppAuth && purchaseMotion === BUY_TOUCHLESS && isMarketingContactsSidegrade && purchaseMotionLocation !== 'PRICING_PAGE_INTERSTITIAL') {
    return /*#__PURE__*/_jsx(UIPurchaseMotionButton, Object.assign({}, filteredPropsForPMB(propsToPass), {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "pricingPage.pricingCtas.switchToMarketingContacts"
      })
    }));
  }
  if (purchaseMotion === TRIAL) {
    if (isMTVTrialButtonUngated) {
      var _props$upgradeData$ap;
      return /*#__PURE__*/_jsx(PurchaseMotionCTA, {
        apiName: (_props$upgradeData$ap = props.upgradeData.apiName) !== null && _props$upgradeData$ap !== void 0 ? _props$upgradeData$ap : UpgradeProductToApiNameMap[props.upgradeData.upgradeProduct],
        location: "MERCH_CARD",
        trackingData: {
          app: props.upgradeData.app,
          screen: props.upgradeData.screen,
          uniqueId: props.upgradeData.uniqueId
        },
        uiButtonProps: Object.assign({}, undefined !== props.block && {
          block: props.block
        }, undefined !== props.use && {
          use: props.use
        }, undefined !== props.size && {
          size: props.size
        }),
        "data-selenium-id": `${trackingSource}-${productIdentifier}-${purchaseMotion}-button`,
        purchaseMotion: purchaseMotion,
        children: trackingSource === MERCH_CARD || trackingSource === PRODUCT_PAGE_FEATURE_TABLE_HEADER || trackingSource === SSM0010_RECOMMENDED_SALES_FOOTER || trackingSource === SSM0010_RECOMMENDED_SALES_HEADER_BANNER ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "pricingPage.pricingCtas.startTrial"
        }) : undefined
      });
    } else if (trackingSource === MERCH_CARD || trackingSource === PRODUCT_PAGE_FEATURE_TABLE_HEADER || trackingSource === SSM0010_RECOMMENDED_SALES_FOOTER || trackingSource === SSM0010_RECOMMENDED_SALES_HEADER_BANNER) {
      return /*#__PURE__*/_jsx(UIPurchaseMotionButton, Object.assign({}, filteredPropsForPMB(propsToPass), {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "pricingPage.pricingCtas.startTrial"
        })
      }));
    }
  }
  return /*#__PURE__*/_jsx(UIPurchaseMotionButton, Object.assign({}, filteredPropsForPMB(propsToPass)));
};
export default PurchaseMotionButtonWrapper;